import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, ShieldAlert, Eye, EyeOff } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";

const ACTIONS = [
  "LOGIN_SUCCESS", "LOGIN_FAILED", "LOGOUT", "PASSWORD_CHANGED", "PASSWORD_RESET",
  "CLOCK_IN", "CLOCK_OUT", "BREAK_START", "BREAK_END", "SHIFT_NOTE", "SHIFT_EDIT", "SHIFT_APPROVE",
  "LEAVE_SUBMITTED", "LEAVE_APPROVE", "LEAVE_DECLINE", "LEAVE_BALANCE_EDIT",
  "USER_CREATE", "USER_UPDATE", "USER_DEACTIVATE", "USER_REACTIVATE", "USER_UNLOCK",
  "ROSTER_VIEW", "ROSTER_SAVED", "ROSTER_PUBLISHED", "ROSTER_EXPORT_PDF",
  "PASSKEY_REGISTER", "PASSKEY_LOGIN", "PASSKEY_REVOKE", "PASSKEY_REVOKE_BY_ADMIN",
  "XERO_CONNECT", "XERO_DISCONNECT", "XERO_SYNC",
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function isoMondayThisWeek() {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [rl, setRl] = useState(null);
  const [filter, setFilter] = useState({
    action: "ALL",
    entity_type: "",
    actor_search: "",
    from_date: "",
    to_date: "",
  });
  const [rosterViews, setRosterViews] = useState(null);
  const [rvWeek, setRvWeek] = useState(isoMondayThisWeek());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (filter.action !== "ALL") params.set("action", filter.action);
        if (filter.entity_type) params.set("entity_type", filter.entity_type);
        if (filter.actor_search) params.set("actor_search", filter.actor_search);
        if (filter.from_date) params.set("from_date", filter.from_date);
        if (filter.to_date) params.set("to_date", filter.to_date);
        const [a, r] = await Promise.all([
          api.get(`/admin/audit?${params}`),
          api.get(`/admin/audit/rate-limit-stats`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setItems(a.data.audit);
        setRl(r.data);
      } catch {
        // silent — filter probably mid-edit
      }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/roster-views?week_start=${rvWeek}`);
        if (!cancelled) setRosterViews(data);
      } catch {
        if (!cancelled) setRosterViews(null);
      }
    })();
    return () => { cancelled = true; };
  }, [rvWeek]);

  const pg = usePagination(items, 12);

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">Audit log</h1>
          <p className="text-gray-500 mt-1">Immutable record of every system event.</p>
        </div>
        <a href={`${API_BASE}/admin/audit/export.csv`} target="_blank" rel="noreferrer">
          <Button variant="outline" data-testid="export-audit"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
        </a>
      </header>

      {rl && (
        <Card data-testid="rate-limit-card" className={rl.blocked_ips > 0 ? "border-red-300" : "border-gray-200"}>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <ShieldAlert className={`h-5 w-5 mt-0.5 shrink-0 ${rl.blocked_ips > 0 ? "text-red-600" : "text-gray-400"}`} />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Login rate-limiter · last {rl.window_minutes} min</div>
                <div className="font-heading text-lg font-bold text-[#1A2744] mt-1">
                  <span data-testid="rl-total-hits">{rl.total_hits}</span> hits
                  <span className="text-gray-400 font-normal"> · </span>
                  <span data-testid="rl-unique-ips">{rl.unique_ips}</span> IPs
                  {rl.blocked_ips > 0 && (
                    <>
                      <span className="text-gray-400 font-normal"> · </span>
                      <span className="text-red-600" data-testid="rl-blocked-ips">{rl.blocked_ips} blocked</span>
                    </>
                  )}
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">Threshold: {rl.threshold} attempts / {rl.window_minutes}min / IP</div>
              </div>
            </div>
            {rl.top.length > 0 && (
              <div className="flex flex-wrap gap-1 max-w-xl justify-end">
                {rl.top.slice(0, 5).map((row) => (
                  <span
                    key={row.ip}
                    className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${row.is_blocked ? "bg-red-50 text-red-700 border-red-200" : "bg-gray-50 text-gray-700 border-gray-200"}`}
                    title={`Last attempt ${row.last_attempt_at ? new Date(row.last_attempt_at).toLocaleString() : "—"}`}
                    data-testid={`rl-top-${row.ip}`}
                  >
                    {row.ip} · {row.count}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label>Action</Label>
            <Select value={filter.action} onValueChange={(v) => setFilter({ ...filter, action: v })}>
              <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="ALL">All actions</SelectItem>
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Actor (name / email / code)</Label>
            <Input
              data-testid="audit-filter-actor"
              placeholder="e.g. Pooja"
              value={filter.actor_search}
              onChange={(e) => setFilter({ ...filter, actor_search: e.target.value })}
            />
          </div>
          <div>
            <Label>From</Label>
            <Input
              type="date"
              data-testid="audit-filter-from"
              value={filter.from_date}
              onChange={(e) => setFilter({ ...filter, from_date: e.target.value })}
            />
          </div>
          <div>
            <Label>To</Label>
            <Input
              type="date"
              data-testid="audit-filter-to"
              value={filter.to_date}
              onChange={(e) => setFilter({ ...filter, to_date: e.target.value })}
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setFilter({ action: "ALL", entity_type: "", actor_search: "", from_date: "", to_date: "" })}
            data-testid="audit-filter-clear"
          >Clear</Button>
        </CardContent>
      </Card>

      <Card data-testid="roster-views-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-[#C9A84C]" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Roster acknowledgement</div>
                <div className="font-heading text-lg font-bold text-[#1A2744]">
                  {rosterViews ? (
                    <>
                      <span data-testid="rv-seen">{rosterViews.seen}</span>
                      <span className="text-gray-400 font-normal"> of </span>
                      <span data-testid="rv-total">{rosterViews.total_staff}</span>
                      <span className="text-gray-400 font-normal"> staff viewed</span>
                    </>
                  ) : "Loading…"}
                </div>
              </div>
            </div>
            <Input
              type="date"
              value={rvWeek}
              onChange={(e) => setRvWeek(e.target.value)}
              className="max-w-[180px]"
              data-testid="rv-week-picker"
            />
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last viewed</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!rosterViews && <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-4">Loading…</TableCell></TableRow>}
                {rosterViews && rosterViews.rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">No active staff for this filter.</TableCell></TableRow>}
                {rosterViews && rosterViews.rows.map((r) => (
                  <TableRow key={r.user_id} data-testid={`rv-row-${r.user_id}`}>
                    <TableCell>
                      <div className="font-medium text-[#1A2744]">{r.name}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{r.employee_code}</div>
                    </TableCell>
                    <TableCell className="text-xs">{r.site || "—"}</TableCell>
                    <TableCell>
                      {r.has_viewed ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                          <Eye className="h-3.5 w-3.5" /> Seen
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
                          <EyeOff className="h-3.5 w-3.5" /> Not seen
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {r.last_viewed_at ? new Date(r.last_viewed_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.view_count || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Entity ID</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No entries.</TableCell></TableRow>}
              {pg.pageItems.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs text-gray-500">{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell>{r.actor_name || <span className="text-gray-400 italic">system</span>}</TableCell>
                  <TableCell><span className="font-mono text-[11px] bg-gray-100 px-2 py-0.5 rounded">{r.action}</span></TableCell>
                  <TableCell>{r.entity_type}</TableCell>
                  <TableCell className="text-[11px] text-gray-500 font-mono">{r.entity_id || "—"}</TableCell>
                  <TableCell className="text-[11px] text-gray-500 font-mono">{r.ip_address || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="audit" />
    </div>
  );
}
